import { Type } from 'class-transformer';
import {
  IsBoolean,
  IsEnum,
  IsNumber,
  IsOptional,
  IsString,
  IsUUID,
  ValidateNested,
} from 'class-validator';
import { type FieldValueSource } from '../../label/field-value-source';
import { NUTRITION_SCOPES, type NutritionScope } from './nutrition-scope.enum';
import { type NutritionalProfileMethod } from './nutritional-profile-method.enum';
import { NutritionalProfileResponseDto } from './nutritional-profile-response.dto';

/** Effective physical factors after applying a tenant override (if any) over
 * the global values. Each value/source is `null` when neither the override nor
 * the global carries it — `ediblePortionFactor` in particular stays null to
 * signal "incomplete", it is never silently defaulted to 1. */
export class EffectivePhysicalFactorsDto {
  @IsOptional()
  @IsNumber()
  densityGPerMl?: number | null;

  @IsOptional()
  @IsString()
  densitySource?: FieldValueSource | null;

  @IsOptional()
  @IsNumber()
  pieceWeightG?: number | null;

  @IsOptional()
  @IsString()
  pieceWeightSource?: FieldValueSource | null;

  @IsOptional()
  @IsNumber()
  ediblePortionFactor?: number | null;

  @IsOptional()
  @IsString()
  ediblePortionSource?: FieldValueSource | null;
}

/**
 * Effective nutrition/physical data for one ingredient as resolved for a
 * tenant, in precedence order: tenant custom Ingredient > the matching global
 * Ingredient with this tenant's sparse override applied > unmodified global
 * Ingredient. `scope` says which branch won, so a caller never re-derives it.
 *
 * `profile` is null when no profile exists at the winning scope (e.g. a global
 * ingredient with no curated profile yet); `profileMethod`/`profileSource`
 * mirror the active profile's metadata when present.
 */
export class EffectiveNutritionResponseDto {
  @IsUUID()
  ingredientId!: string;

  @IsString()
  name!: string;

  @IsBoolean()
  isGlobalCatalogEntry!: boolean;

  @IsEnum(NUTRITION_SCOPES)
  scope!: NutritionScope;

  @IsOptional()
  @ValidateNested()
  @Type(() => NutritionalProfileResponseDto)
  profile?: NutritionalProfileResponseDto | null;

  @IsOptional()
  @IsString()
  profileMethod?: NutritionalProfileMethod | null;

  @IsOptional()
  @IsString()
  profileSourceReference?: string | null;

  @ValidateNested()
  @Type(() => EffectivePhysicalFactorsDto)
  physical!: EffectivePhysicalFactorsDto;

  /**
   * True only when `scope` is `GLOBAL_OVERRIDE` AND the tenant override
   * actually carries nutrition-related data (alias, a physical factor, or a
   * linked profile). A tenant override row can exist with only an organic
   * declaration on it (art. 30 Reg. (UE) 2018/848, unrelated to nutrition),
   * in which case `scope` alone still reads `GLOBAL_OVERRIDE` even though
   * there is nothing left for "restore Prospero values" to reset. A caller
   * that wants to know whether resetting nutrition would do anything MUST
   * check this flag instead of `scope === 'GLOBAL_OVERRIDE'` alone.
   */
  @IsBoolean()
  hasNutritionOverride!: boolean;
}
